/**
 * Decodifica un string base64 a UTF-8.
 * @param {string} base64 - El string en base64.
 * @returns {string} El texto decodificado.
 */
export const decodeBase64Utf8 = (base64) => {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(bytes);
  } catch (e) {
    try {
      return atob(base64);
    } catch (err) {
      return 'Error al decodificar XML.';
    }
  }
};

/**
 * Parsea XML de factura electrónica usando DOMParser con fallback por regex.
 * @param {string} xmlText - El texto XML a parsear.
 * @returns {object} Datos extraídos del XML: { success, id, issueDate, ... }
 */
export const parseXmlData = (xmlText) => {
  let domData = { success: false };
  try {
    if (!xmlText || typeof xmlText !== 'string') return domData;

    // Si es un AttachedDocument de la DIAN con CDATA o XML incrustado, extraer el XML interno (Invoice / CreditNote)
    let textToParse = xmlText;
    if (xmlText.includes('AttachedDocument')) {
      const cdataMatch = xmlText.match(/<!\[CDATA\[([\s\S]*?)\]\]>/i) || xmlText.match(/<cbc:Description>([\s\S]*?)<\/cbc:Description>/i);
      if (cdataMatch && cdataMatch[1] && (cdataMatch[1].includes('<Invoice') || cdataMatch[1].includes('<CreditNote') || cdataMatch[1].includes('<DebitNote'))) {
        textToParse = cdataMatch[1].trim();
      }
    }

    const parser = new DOMParser();
    let xmlDoc = parser.parseFromString(textToParse, "text/xml");

    const getTagContent = (docOrEl, tagName) => {
      if (!docOrEl) return '';
      let elements = docOrEl.getElementsByTagNameNS("*", tagName);
      if (elements.length === 0) {
        elements = docOrEl.getElementsByTagNameNS("*", tagName.toLowerCase());
      }
      if (elements.length === 0) {
        elements = docOrEl.getElementsByTagName(tagName);
      }
      return elements.length > 0 ? elements[0].textContent.trim() : '';
    };

    const getTagElement = (docOrEl, tagName) => {
      if (!docOrEl) return null;
      let elements = docOrEl.getElementsByTagNameNS("*", tagName);
      if (elements.length === 0) {
        elements = docOrEl.getElementsByTagNameNS("*", tagName.toLowerCase());
      }
      if (elements.length === 0) {
        elements = docOrEl.getElementsByTagName(tagName);
      }
      return elements.length > 0 ? elements[0] : null;
    };

    const getMainTagContent = (doc, tagNames) => {
      if (!doc) return '';
      const root = doc.documentElement || doc;
      const tags = Array.isArray(tagNames) ? tagNames : [tagNames];

      // 1. Direct children
      for (let i = 0; i < root.children.length; i++) {
        const child = root.children[i];
        const name = child.localName || child.tagName;
        if (tags.includes(name)) {
          const val = child.textContent?.trim();
          if (val) return val;
        }
      }

      // 2. Filtered nodes
      for (const tag of tags) {
        let elements = Array.from(root.getElementsByTagNameNS("*", tag));
        if (elements.length === 0) elements = Array.from(root.getElementsByTagNameNS("*", tag.toLowerCase()));
        for (const el of elements) {
          let p = el.parentNode;
          let skip = false;
          while (p && p !== root) {
            const pName = p.localName || p.tagName;
            if (["UBLExtension", "InvoiceLine", "CreditNoteLine", "DebitNoteLine", "PaymentMeans", "Party", "TaxScheme", "Item", "StandardItemIdentification", "SellersItemIdentification"].includes(pName)) {
              skip = true;
              break;
            }
            p = p.parentNode;
          }
          if (!skip && el.textContent?.trim()) {
            return el.textContent.trim();
          }
        }
      }

      return getTagContent(doc, tags[0]);
    };

    const id = getMainTagContent(xmlDoc, ['ParentDocumentID', 'ID']);
    const issueDate = getTagContent(xmlDoc, 'IssueDate');
    const issueTime = getTagContent(xmlDoc, 'IssueTime');
    const documentCurrencyCode = getTagContent(xmlDoc, 'DocumentCurrencyCode');

    // --- Emisor / Proveedor ---
    let supplierName = '';
    let supplierId = '';
    const supplierParty = getTagElement(xmlDoc, 'AccountingSupplierParty') || getTagElement(xmlDoc, 'SenderParty');
    if (supplierParty) {
      supplierName =
        getTagContent(supplierParty, 'RegistrationName') ||
        getTagContent(supplierParty, 'Name') ||
        getTagContent(supplierParty, 'CompanyID');
      supplierId =
        getTagContent(supplierParty, 'CompanyID') ||
        getTagContent(supplierParty, 'ID');
    }

    // --- Adquirente / Cliente ---
    let customerName = '';
    let customerId = '';
    const customerParty = getTagElement(xmlDoc, 'AccountingCustomerParty') || getTagElement(xmlDoc, 'ReceiverParty');
    if (customerParty) {
      customerName =
        getTagContent(customerParty, 'RegistrationName') ||
        getTagContent(customerParty, 'Name') ||
        getTagContent(customerParty, 'CompanyID');
      customerId =
        getTagContent(customerParty, 'CompanyID') ||
        getTagContent(customerParty, 'ID');
    }

    // --- Totales ---
    const legalMonetaryTotal = getTagElement(xmlDoc, 'LegalMonetaryTotal') || getTagElement(xmlDoc, 'RequestedMonetaryTotal');
    let lineExtensionAmount = '';
    let taxExclusiveAmount = '';
    let taxInclusiveAmount = '';
    let allowanceTotalAmount = '';
    let payableAmount = '';
    if (legalMonetaryTotal) {
      lineExtensionAmount = getTagContent(legalMonetaryTotal, 'LineExtensionAmount');
      taxExclusiveAmount = getTagContent(legalMonetaryTotal, 'TaxExclusiveAmount');
      taxInclusiveAmount = getTagContent(legalMonetaryTotal, 'TaxInclusiveAmount');
      allowanceTotalAmount = getTagContent(legalMonetaryTotal, 'AllowanceTotalAmount');
      payableAmount = getTagContent(legalMonetaryTotal, 'PayableAmount');
    }

    // Fallbacks por regex para totales si el DOMParser no capturó
    if (!payableAmount) {
      const match = textToParse.match(/<[^:]*:PayableAmount[^>]*>([^<]+)<\/[^:]*:PayableAmount>/i);
      if (match) payableAmount = match[1].trim();
    }
    if (!lineExtensionAmount) {
      const match = textToParse.match(/<[^:]*:LineExtensionAmount[^>]*>([^<]+)<\/[^:]*:LineExtensionAmount>/i);
      if (match) lineExtensionAmount = match[1].trim();
    }
    if (!taxInclusiveAmount) {
      const match = textToParse.match(/<[^:]*:TaxInclusiveAmount[^>]*>([^<]+)<\/[^:]*:TaxInclusiveAmount>/i);
      if (match) taxInclusiveAmount = match[1].trim();
    }

    // --- Ítems ---
    let lineElements = xmlDoc.getElementsByTagNameNS("*", 'InvoiceLine');
    if (lineElements.length === 0) lineElements = xmlDoc.getElementsByTagNameNS("*", 'CreditNoteLine');
    if (lineElements.length === 0) lineElements = xmlDoc.getElementsByTagNameNS("*", 'DebitNoteLine');

    const lineItems = [];
    for (let i = 0; i < lineElements.length; i++) {
      const line = lineElements[i];
      const itemEl = getTagElement(line, 'Item');
      const priceEl = getTagElement(line, 'Price');
      const name = itemEl ? (getTagContent(itemEl, 'Description') || getTagContent(itemEl, 'Name')) : getTagContent(line, 'Description');
      const quantity = getTagContent(line, 'InvoicedQuantity') || getTagContent(line, 'CreditedQuantity') || getTagContent(line, 'DebitedQuantity') || getTagContent(line, 'Quantity') || '1';
      const price = priceEl ? getTagContent(priceEl, 'PriceAmount') : getTagContent(line, 'LineExtensionAmount');
      const total = getTagContent(line, 'LineExtensionAmount') || (Number(quantity) * Number(price)).toString();

      lineItems.push({
        name: name || `Ítem #${i + 1}`,
        quantity,
        price,
        total
      });
    }

    domData = {
      success: true,
      id: id || getTagContent(xmlDoc, 'ID'),
      issueDate: issueDate || getTagContent(xmlDoc, 'IssueDate'),
      issueTime,
      documentCurrencyCode,
      supplierName,
      supplierId,
      customerName,
      customerId,
      lineExtensionAmount,
      taxExclusiveAmount,
      taxInclusiveAmount,
      allowanceTotalAmount,
      payableAmount: payableAmount || taxInclusiveAmount || lineExtensionAmount,
      items: lineItems
    };
  } catch (e) {
    try {
      const getRegexTagContent = (text, tagName) => {
        const regex = new RegExp(`<[^:]*:${tagName}[^>]*>([^<]+)</[^:]*:${tagName}>`, 'i');
        const match = text.match(regex);
        return match ? match[1].trim() : '';
      };

      domData = {
        success: true,
        id: getRegexTagContent(xmlText, 'ID'),
        issueDate: getRegexTagContent(xmlText, 'IssueDate'),
        payableAmount: getRegexTagContent(xmlText, 'PayableAmount'),
        items: []
      };
    } catch (regexError) {
      domData = { success: false, error: 'No se pudo parsear el XML.' };
    }
  }
  return domData;
};


/**
 * Compara dos identificaciones (NIT/documento) normalizadas.
 * @param {string} id1
 * @param {string} id2
 * @returns {boolean}
 */
export const matchIdentifications = (id1, id2) => {
  if (!id1 || !id2) return false;
  const clean1 = String(id1).replace(/[^0-9]/g, '');
  const clean2 = String(id2).replace(/[^0-9]/g, '');
  if (!clean1 || !clean2) return false;
  if (clean1 === clean2) return true;
  if (clean1.length >= 9 && clean2.length >= 9) {
    return clean1.substring(0, 9) === clean2.substring(0, 9);
  }
  return false;
};
